import { createSlice } from "@reduxjs/toolkit";
import { moveChoiceAdapter } from "../../controller/actions/node/choice/adapters/moveChoiceAdapter";
import { addChoice } from "../../controller/actions/node/choice/addChoice";
import { copyChoice } from "../../controller/actions/node/choice/copyChoice";
import { removeChoice } from "../../controller/actions/node/choice/removeChoice";
import { updateChoiceName } from "../../controller/actions/node/choice/updateChoiceName";
import { updateChoiceValue } from "../../controller/actions/node/choice/updateChoiceValue";
import { moveConditionAdapter } from "../../controller/actions/node/condition/adapters/moveConditionAdapter";
import { addCondition } from "../../controller/actions/node/condition/addCondition";
import { copyCondition } from "../../controller/actions/node/condition/copyCondition";
import { removeCondition } from "../../controller/actions/node/condition/removeCondition";
import { updateConditionExpression } from "../../controller/actions/node/condition/updateConditionExpression";
import { updateConditionName } from "../../controller/actions/node/condition/updateConditionName";
import { moveFunctionAdapter } from "../../controller/actions/node/function/adapters/moveFunctionAdapter";
import { addFunction } from "../../controller/actions/node/function/addFunction";
import { copyFunction } from "../../controller/actions/node/function/copyFunction";
import { removeFunction } from "../../controller/actions/node/function/removeFunction";
import { updateFunctionName } from "../../controller/actions/node/function/updateFunctionName";
import { historyUndo } from "../../controller/actions/node/history/historyUndo";
import { addParameterAdapter } from "../../controller/actions/node/parameter/adapters/addParameterAdapter";
import { moveParameterAdapter } from "../../controller/actions/node/parameter/adapters/moveParameterAdapter";
import { copyParameter } from "../../controller/actions/node/parameter/copyParameter";
import { detachParameter } from "../../controller/actions/node/parameter/detachParameter";
import { removeParameter } from "../../controller/actions/node/parameter/removeParameter";
import { updateParameterName } from "../../controller/actions/node/parameter/updateParameterName";
import { updateParameterValueDefault } from "../../controller/actions/node/parameter/updateParameterValueDefault";
import { editModel } from "../../controller/actions/node/root/editModel";
import { loadModel } from "../../controller/actions/node/root/loadModel";
import { nodeUpdateComment } from "../../controller/actions/node/universal/updateNodeComment";
import { nodeUpdateLabel } from "../../controller/actions/node/universal/updateNodeLabel";
import { getName } from "../../controller/nodeAccessor";
import * as factory from "../../controller/nodeFactory";
import { getNodeById, getNodeWithTraversalById } from "../../controller/nodeSelector";
import * as historyUtils from "../../controller/utils/historyUtils";
import * as nodeUtils from "../../controller/utils/nodeUtils";
import * as pathUtils from "../../controller/utils/pathUtils";
import * as systemUtils from "../../controller/utils/systemUtils";
import * as typeUtils from "../../controller/utils/typeUtils";

const initialState = {
  root: {},
  idFunction: "",
  history: [],
  historyLastPayload: "",
};

const structureSlice = createSlice({
  name: "structure",
  initialState,
  reducers: {
    setRoot: {
      prepare(root, index, skip) {
        return {
          payload: { root, index, skip },
        };
      },
      reducer(state, action) {
        if (systemUtils.isNumber(action.payload.index) && action.payload.index !== -1) {
          state.idFunction = nodeUtils.getFirstFunctionIndex(
            action.payload.root,
            action.payload.index
          );
        }
        const { root, info } = loadModel(action.payload.root);


        if (!action.payload.skip) {

          state.historyLastPayload = info.description;
          historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);
        }

        state.root = root;
      },
    },
    editRoot: {
      prepare(root, index, description) {
        return {
          payload: { root, index, description },
        };
      },
      reducer(state, action) {console.log(action.payload.index)
        if (systemUtils.isNumber(action.payload.index)) {
          let index = action.payload.index;

          if (index > nodeUtils.getFunctions(action.payload.root).length) {
            index = 0;
          }

          state.idFunction = nodeUtils.getFirstFunctionIndex(
            action.payload.root,
            index
          );
        }

        const { root, info } = editModel(action.payload.root);

        state.historyLastPayload = action.payload.description || info.description;
        historyUtils.pushHistoryEvent(
          { ...info, description: action.payload.description || info.description },
          root,
          state.history,
          state.idFunction
        );

        state.root = root;
      },
    },
    rewindHistory: {
      prepare(time) {
        return {
          payload: { time },
        };
      },
      reducer(state, action) {
        const { root, info } = historyUndo(action.payload.time, state.history);
        historyUtils.pushHistoryEvent(info, state.root, state.history, state.idFunction);
        
        if (root) {
          state.root = root;

          if (info.idFunction) {  
            state.idFunction = info.idFunction;
          }
        }
      },
    },
    //-----------------------------------------------------------------------------------
    setIdFunction(state, action) {
      state.idFunction = action.payload;
    },
    //-----------------------------------------------------------------------------------
    handleParameterCreate: {
      prepare(id, forceIsTop) {
        return {
          payload: { id, forceIsTop },
        };
      },
      reducer(state, action) {
        const { root, info } = addParameterAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          typeUtils.getTypeParameter(),
          action.payload.forceIsTop
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleParameterOutputCreate: {
      prepare(id, forceIsTop) {
        return {
          payload: { id, forceIsTop },
        };
      },
      reducer(state, action) {
        const { root, info } = addParameterAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          typeUtils.getTypeParameterOutput(),
          action.payload.forceIsTop
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleParameterRemove: {
      prepare(id, keep, remove) {
        return {
          payload: { id, keep, remove },
        };
      },
      reducer(state, action) {
        const { root, info } = removeParameter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.keep,
          action.payload.remove,
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleParameterMove: {
      prepare(id, idDestination, forceIsTop, forceLink) {
        return {
          payload: { id, idDestination, forceIsTop, forceLink },
        };
      },
      reducer(state, action) {
        const { root, info } = moveParameterAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.idDestination,
          action.payload.forceIsTop,
          action.payload.forceLink
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleParameterRename: {
      prepare(id, name) {
        return {
          payload: { id, name },
        };
      },
      reducer(state, action) {
        const { root, info } = updateParameterName(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.name,
          state.historyLastPayload
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleParameterCopy(state, action) {
      const { root, info } = copyParameter(systemUtils.createDeepCopy(state.root), action.payload);

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleParameterDetach(state, action) {
      const { root, info } = detachParameter(
        systemUtils.createDeepCopy(state.root),
        action.payload
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleParameterUpdateOutput: {
      prepare(id, value) {
        return {
          payload: { id, value },
        };
      },
      reducer(state, action) {
        const { root, info } = updateParameterValueDefault(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.value
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    //--------------------------------------------------
    handleChoiceCreate(state, action) {
      const { root, info } = addChoice(
        systemUtils.createDeepCopy(state.root),
        action.payload,
        factory.createChoice(action.payload)
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleChoiceRemove: {
      prepare(id, keep) {
        return {
          payload: { id, keep },
        };
      },
      reducer(state, action) {
        const { root, info } = removeChoice(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.keep
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleChoiceMove: {
      prepare(id, idDestination, forceIsTop, offset = 0) {
        return {
          payload: { id, idDestination, forceIsTop, offset },
        };
      },
      reducer(state, action) {
        const { root, info } = moveChoiceAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.idDestination,
          action.payload.forceIsTop,
          action.payload.offset
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleChoiceRename: {
      prepare(id, name) {
        return {
          payload: { id, name },
        };
      },
      reducer(state, action) {
        const { root, info } = updateChoiceName(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.name,
          state.historyLastPayload
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleChoiceCopy(state, action) {
      const { root, info } = copyChoice(systemUtils.createDeepCopy(state.root), action.payload);

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleChoiceUpdateValue: {
      prepare(id, value) {
        return {
          payload: { id, value },
        };
      },
      reducer(state, action) {
        const { root, info } = updateChoiceValue(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.value
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleChoiceRenameAndCreate: {
      prepare(id, name) {
        return {
          payload: { id, name },
        };
      },
      reducer(state, action) {
        const _idParent = pathUtils.getIdParent(action.payload.id);
        const _index = getNodeById(state.root, action.payload.id)?.index + 1;

        const { root: rootA, info: infoA } = updateChoiceName(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.name,
          state.historyLastPayload
        );

        state.historyLastPayload = infoA;
        historyUtils.pushHistoryEvent(infoA, rootA, state.history, state.idFunction);
        
        state.root = rootA;

        const { root: rootB, info: infoB } = addChoice(
          systemUtils.createDeepCopy(state.root),
          _idParent,
          factory.createChoice(_idParent),
          _index
        );

        state.historyLastPayload = infoB;
        historyUtils.pushHistoryEvent(infoB, rootB, state.history, state.idFunction);

        state.root = rootB;
      },
    },
    //--------------------------------------------------
    handleNodeUpdateLabel: {
      prepare(id, label) {
        return {
          payload: { id, label },
        };
      },
      reducer(state, action) {
        const { root, info } = nodeUpdateLabel(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.label
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleNodeUpdateComment: {
      prepare(id, comment) {
        return {
          payload: { id, comment },
        };
      },
      reducer(state, action) {
        const { root, info } = nodeUpdateComment(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.comment
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    //-----------------------------------------------------------------------------------
    handleFunctionCreate(state) {
      const { root, info } = addFunction(
        systemUtils.createDeepCopy(state.root),
        factory.createFunction()
      );
      state.idFunction = nodeUtils.getID(nodeUtils.getLastFunction(root));

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleFunctionRemove(state, action) {
      const { root, info } = removeFunction(systemUtils.createDeepCopy(state.root), action.payload);
      state.idFunction = nodeUtils.getFirstFunctionIndex(root, 0);

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleFunctionMove: {
      prepare(id, idDestination) {
        return {
          payload: { id, idDestination },
        };
      },
      reducer(state, action) {
        const { root, info } = moveFunctionAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.idDestination
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleFunctionRename: {
      prepare(id, name) {
        return {
          payload: { id, name },
        };
      },
      reducer(state, action) {
        const id = pathUtils.getIdElement(action.payload.name, typeUtils.getTypeFunction());
        const { root, info } = updateFunctionName(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.name,
          state.historyLastPayload
        );
        state.idFunction = id;

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleFunctionCopy(state, action) {
      const { root, info } = copyFunction(systemUtils.createDeepCopy(state.root), action.payload);

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    //-----------------------------------------------------------------------------------
    handleConditionAliasCreate(state, action) {
      const body = factory.createConditionAlias(action.payload);
      const { root, info } = addCondition(
        systemUtils.createDeepCopy(state.root),
        action.payload,
        body
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleConditionConstraintCreate(state, action) {
      const body = factory.createConditionConstraint(action.payload);
      const { root, info } = addCondition(
        systemUtils.createDeepCopy(state.root),
        action.payload,
        body
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleConditionAssignmentCreate(state, action) {
      const body = factory.createConditionAssignment(action.payload);
      const { root, info } = addCondition(
        systemUtils.createDeepCopy(state.root),
        action.payload,
        body
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleConditionRename: {
      prepare(id, name) {
        return {
          payload: { id, name },
        };
      },
      reducer(state, action) {
        const { root, info } = updateConditionName(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.name,
          state.historyLastPayload
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleConditionCopy(state, action) {
      const { root, info } = copyCondition(systemUtils.createDeepCopy(state.root), action.payload);

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleConditionRemove(state, action) {
      const { root, info } = removeCondition(
        systemUtils.createDeepCopy(state.root),
        action.payload
      );

      state.historyLastPayload = info;
      historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

      state.root = root;
    },
    handleConditionMove: {
      prepare(id, idDestination, forceIsTop, offset = 0) {
        return {
          payload: { id, idDestination, forceIsTop, offset },
        };
      },
      reducer(state, action) {
        const { root, info } = moveConditionAdapter(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.idDestination,
          action.payload.forceIsTop,
          action.payload.offset
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    handleConditionUpdateExpression: {
      prepare(id, expression) {
        return {
          payload: { id, expression },
        };
      },
      reducer(state, action) {
        const { root, info } = updateConditionExpression(
          systemUtils.createDeepCopy(state.root),
          action.payload.id,
          action.payload.expression
        );

        state.historyLastPayload = info;
        historyUtils.pushHistoryEvent(info, root, state.history, state.idFunction);

        state.root = root;
      },
    },
    //-----------------------------------------------------------------------------------
  },
});

//-----------------------------------------------------------------------------------

export const getRoot = (state) => {
  return state.structure.root;
};

export const getIdFunction = (state) => {
  return state.structure.idFunction;
};

export const getIndexFunction = (state) => {
  return state.structure.idFunction;
};

export const getHistory = (state) => {
  return state.structure.history;
};

export const getNodeStructure = (id) => (state) => {
  return getNodeById(state.structure.root, id)?.data;
};

export const getNodeStructureWithTraversal = (id) => (state) => {
  return getNodeWithTraversalById(state.structure.root, id)?.data;
};

export const hasEmptyNode = (state) => {
  let isEmpty = false;

  nodeUtils.visitElements(state.structure.root, e => {
    if (getName(e) === "") {
      isEmpty = true;
    }
  })

  return isEmpty;
}

//-----------------------------------------------------------------------------------

export default structureSlice.reducer;
export const {
  setRoot,
  editRoot,
  setIdFunction,
  rewindHistory,
  //--------------------------------------------------
  handleParameterCreate,
  handleParameterOutputCreate,
  handleParameterRemove,
  handleParameterMove,
  handleParameterRename,
  handleParameterCopy,
  handleParameterDetach,
  handleParameterUpdateOutput,
  handleChoiceCreate,
  handleChoiceRemove,
  handleChoiceMove,
  handleChoiceRename,
  handleChoiceCopy,
  handleChoiceUpdateValue,
  handleChoiceRenameAndCreate,
  handleFunctionCreate,
  handleFunctionRemove,
  handleFunctionMove,
  handleFunctionRename,
  handleFunctionCopy,
  handleConditionAliasCreate,
  handleConditionConstraintCreate,
  handleConditionAssignmentCreate,
  handleConditionRemove,
  handleConditionMove,
  handleConditionRename,
  handleConditionCopy,
  handleConditionRenameAndExpressionCreate,
  handleConditionUpdateExpression,
  handleNodeUpdateLabel,
  handleNodeUpdateComment
} = structureSlice.actions;
